import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItemButton,
  ListItemText,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import BusinessMapTab from '../zones/BusinessMapTab';
import DeviceTree from '../devices/DeviceTree';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`business-tabpanel-${index}`}
      aria-labelledby={`business-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Business {
  id: string;
  name: string;
  abn: string;
  primaryUserId: string;
  address?: {
    street: string;
    city: string;
    state: string;
    postCode: string;
    country: string;
  };
}

interface BusinessMember {
  id: string;
  email: string;
  fullName: string;
  isActive: boolean;
}

interface Device {
  id: string;
  name: string;
  parentId: string | null;
  deviceType?: {
    name: string;
    deviceCategory?: string | null;
  };
  isActive: boolean;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

interface BusinessAssociation {
  id: string;
  parentBusinessId: string;
  parentBusinessName: string;
  childBusinessId: string;
  childBusinessName: string;
}

const UserBusinessDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const navigate = useNavigate();
  const [business, setBusiness] = useState<Business | null>(null);
  const [devices, setDevices] = useState<Device[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [members, setMembers] = useState<BusinessMember[]>([]);
  const [parentAssociation, setParentAssociation] = useState<BusinessAssociation | null>(null);
  const [childAssociations, setChildAssociations] = useState<BusinessAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(2);
  const [reassigning, setReassigning] = useState(false);
  const [reassignError, setReassignError] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch business details
      const businessResponse = await fetch(`/api/businesses/${id}`, {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (!businessResponse.ok) {
        if (businessResponse.status === 403) {
          throw new Error('You do not have access to this business');
        }
        throw new Error('Failed to fetch business details');
      }

      const businessData = await businessResponse.json();
      setBusiness(businessData);

      // Fetch devices (scoped to this business on backend — GetDevices'
      // businessId filter walks child devices too, so devices that inherit
      // access via a linked parent rather than their own BusinessDevice link
      // still show up here).
      const devicesResponse = await fetch(`/api/devices?businessId=${id}`, {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (devicesResponse.ok) {
        const devicesData = await devicesResponse.json();
        setDevices(devicesData);
      }

      // Fetch contacts (scoped to this business on backend)
      const contactsResponse = await fetch(`/api/contacts?businessId=${id}`, {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });

      if (contactsResponse.ok) {
        const contactsData = await contactsResponse.json();
        setContacts(contactsData);
      }

      // Fetch members (for primary-user reassignment) — best-effort, not
      // fatal if it fails (e.g. gated by an inactive subscription).
      const usersResponse = await fetch(`/api/businesses/${id}/users`, {
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
        },
      });
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setMembers(usersData);
      }

      // Fetch group structure — best-effort, not fatal if it fails.
      const [parentResponse, childrenResponse] = await Promise.all([
        fetch(`/api/businesses/${id}/parent`, {
          headers: { 'Authorization': `Bearer ${auth.tokens?.accessToken}` },
        }),
        fetch(`/api/businesses/${id}/children`, {
          headers: { 'Authorization': `Bearer ${auth.tokens?.accessToken}` },
        }),
      ]);
      if (parentResponse.ok) {
        // /parent returns 204 (empty body) rather than a JSON `null` when there is no parent
        const parentText = await parentResponse.text();
        setParentAssociation(parentText ? JSON.parse(parentText) : null);
      }
      if (childrenResponse.ok) {
        setChildAssociations(await childrenResponse.json());
      }

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleReassignPrimaryUser = async (newPrimaryUserId: string) => {
    if (!business || newPrimaryUserId === business.primaryUserId) return;
    setReassigning(true);
    setReassignError(null);
    try {
      const response = await fetch(`/api/businesses/${business.id}/primary-user`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${auth.tokens?.accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: newPrimaryUserId }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reassign primary user');
      }
      setBusiness(data);
    } catch (err) {
      setReassignError(err instanceof Error ? err.message : 'Failed to reassign primary user');
    } finally {
      setReassigning(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !business) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || 'Business not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/businesses')}
          sx={{ mt: 2 }}
        >
          Back to Businesses
        </Button>
      </Box>
    );
  }

  const isMapTabActive = tabValue === 2;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: isMapTabActive ? 'calc(100vh - 64px)' : 'auto' }}>
      <Paper sx={{ px: 2, py: 1, mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: 1, gap: 1.5, flexShrink: 0 }}>
        <IconButton size="small" onClick={() => navigate('/businesses')} title="Back to Businesses">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" component="h1" fontWeight={600} noWrap>
            {business.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            ABN: {business.abn}
            {business.address && ` · ${business.address.street}, ${business.address.city}, ${business.address.state} ${business.address.postCode}`}
          </Typography>
        </Box>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">Primary User:</Typography>
          {auth.hasRole('DMO_ADMIN') || business.primaryUserId === auth.user?.id ? (
            <FormControl size="small" sx={{ minWidth: 180 }} disabled={reassigning || members.length === 0}>
              <Select
                value={business.primaryUserId}
                onChange={(e) => handleReassignPrimaryUser(e.target.value)}
              >
                {members.map((m) => (
                  <MenuItem key={m.id} value={m.id}>{m.fullName || m.email}</MenuItem>
                ))}
                {!members.some((m) => m.id === business.primaryUserId) && (
                  <MenuItem value={business.primaryUserId}>
                    {members.length === 0 ? 'Loading…' : 'Unknown user'}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="caption">
              {members.find((m) => m.id === business.primaryUserId)?.fullName || '—'}
            </Typography>
          )}
        </Box>
      </Paper>
      {reassignError && <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>{reassignError}</Alert>}

      <Paper
        sx={
          isMapTabActive
            ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
            : undefined
        }
      >
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="business detail tabs" sx={{ flexShrink: 0 }}>
          <Tab label={`Devices (${devices.length})`} />
          <Tab label={`Contacts (${contacts.length})`} />
          <Tab label="Map / Records" />
          {(parentAssociation || childAssociations.length > 0) && (
            <Tab label={`Group${childAssociations.length > 0 ? ` (${childAssociations.length})` : ''}`} />
          )}
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Devices
          </Typography>
          <DeviceTree devices={devices} />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Contacts
          </Typography>
          {contacts.length === 0 ? (
            <Alert severity="info">No contacts found for this business</Alert>
          ) : (
            <Box>
              {contacts.map((contact) => (
                <Paper key={contact.id} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {contact.firstName} {contact.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Email: {contact.email}
                  </Typography>
                  {contact.phone && (
                    <Typography variant="body2" color="text.secondary">
                      Phone: {contact.phone}
                    </Typography>
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </TabPanel>

        {tabValue === 2 && (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <BusinessMapTab businessId={business.id} businessName={business.name} />
          </Box>
        )}

        {(parentAssociation || childAssociations.length > 0) && (
          <TabPanel value={tabValue} index={3}>
            {parentAssociation && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Parent Business
                </Typography>
                <Chip
                  icon={<BusinessIcon />}
                  label={parentAssociation.parentBusinessName}
                  variant="outlined"
                  onClick={() => navigate(`/businesses/${parentAssociation.parentBusinessId}`)}
                  clickable
                />
              </Box>
            )}

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Child Businesses
            </Typography>
            {childAssociations.length === 0 ? (
              <Alert severity="info">This business has no child businesses</Alert>
            ) : (
              <List disablePadding>
                {childAssociations.map((child) => (
                  <ListItemButton
                    key={child.id}
                    onClick={() => navigate(`/businesses/${child.childBusinessId}`)}
                    divider
                  >
                    <ListItemText primary={child.childBusinessName} />
                  </ListItemButton>
                ))}
              </List>
            )}
          </TabPanel>
        )}
      </Paper>
    </Box>
  );
};

export default UserBusinessDetails;
